/**
 * Admin Metrics Service (US-17-GR-24)
 *
 * Calls the live admin metrics endpoints on TaskManager.API. The ChellessAdmin
 * Vite proxy (vite.config.ts) routes /api → the TaskManager API, so relative
 * paths work in both dev and production.
 *
 * All functions use the established authenticatedFetch pattern from src/api/,
 * which injects the admin Bearer token automatically. The endpoints are
 * [Authorize(Roles = "Admin")] — a non-admin token yields 401/403.
 *
 * Recovered and re-homed from the deleted user-app service
 * (frontend commit 9c418bd3f^ src/services/adminMetricsService.ts). Interfaces
 * are aligned 1-to-1 with the current C# DTOs in TaskManager.Models — do NOT
 * regress the US-17-GR-20/21/22/23 alignment fixes:
 *   - AdminMetricsResponse / EngagementMetrics       (AdminMetricsResponse.cs)
 *   - RetentionSeriesResponse                         (AdminMetricsResponse.cs)
 *   - OutsourcingMetricsResponse / ServiceCategoryCount (AdminMetricsResponse.cs)
 *   - RevenueMetricsResponse / TierDistribution       (NpsModels.cs)
 *   - NpsMetricsResponse                              (NpsModels.cs)
 *
 * ASP.NET serialises to camelCase by default — field names below match the
 * serialised camelCase form of the C# property names.
 */

import { authenticatedFetch } from '../api/authenticatedFetch';
import { COLDSTART_RETRY_CONFIG } from '../api/resilientFetch';

export type MetricWindow = 7 | 30 | 90;

// ---------------------------------------------------------------------------
// Response shapes — aligned to TaskManager.Models DTOs (camelCase serialised)
// ---------------------------------------------------------------------------

/** Nested engagement KPIs from EngagementMetrics (AdminMetricsResponse.cs). */
export interface EngagementMetrics {
  /** Weekly Active Users — distinct users active in the last 7 days. */
  wau: number;
  /** Average completed user-driven tasks per WAU member per week. */
  tasksPerActiveUserPerWeek: number;
  /**
   * Same figure as `StreakMetrics.threePlusStreakRate` (DEC-2026-10-06): accounts on a current
   * 3+ day completion streak, as a share of accounts with a card move in 7 days.
   */
  threeDayStreakMaintenance: number;
  /**
   * Easy Button adoption (DEC-2026-09-08 Ruling D): distinct accounts using the Easy Button in
   * the trailing 7 days, as a percent 0-100 of Ruling C active accounts. null = no data OR fault
   * (see `easyButtonAdoptionFault`). PRD target: 30%.
   */
  easyButtonAdoption: number | null;
  /** Distinct accounts using the Easy Button in the trailing 7 days. Absent on an older API. */
  easyButtonAdoptionNumerator?: number | null;
  /** Active accounts (Ruling C) in the same window. Absent on an older API. */
  easyButtonAdoptionDenominator?: number | null;
  /** True when the computed value fell outside 0-100; the value is withheld. Absent on an older API. */
  easyButtonAdoptionFault?: boolean;
  /**
   * True when `easyButtonAdoption` fell back to a proxy (users with >= 5
   * completed tasks) because the real generation records could not be read.
   * False means it is a measured figure — the normal path (US-17-GR-37).
   */
  easyButtonAdoptionIsEstimate: boolean;
  /** PRD target for WAU as a percentage of registered users. */
  wauTarget: number;
  /** null: no target is set. The unratified 65% was removed by DEC-2026-10-06. */
  streakTarget: number | null;
  /** PRD target for Easy Button adoption percentage. */
  easyButtonTarget: number;
}

/**
 * First-sale KPIs from FirstSaleMetrics (AdminMetricsResponse.cs), built by US-17-GR-39.
 *
 * US-17-GR-45 (work item 16493): this block has reached the wire on every call to
 * `GET /api/admin/metrics` since it was built, and this interface did not exist — so the client
 * dropped the whole thing at the type boundary and no component ever saw it. Same shape of bug as
 * the missing `pro` / `unknown` tier buckets noted in AnalyticsPage.test.tsx.
 *
 * The two nullable fields are nullable ON PURPOSE and must never render as 0; the C# XML docs
 * prescribe the exact wording, and FirstSalePanel.tsx uses it.
 */
export interface FirstSaleMetrics {
  // The API serializes with IgnoreNullValues, so a null property is omitted on the wire: absent means null.
  /** Denominator: users registered within the requested window. */
  usersWithin90Days: number;
  /** DISTINCT profiles with a confirmed first-sale mark in the window. A USER count. */
  usersAchievedFirstSale: number;
  firstSaleRate: number;
  /**
   * null, never a fabricated 0 — `Card` records no sale amount, so there is no honest way to
   * compute this. Render "Not tracked".
   */
  averageRevenueAtFirstSale?: number | null;
  /**
   * null while there is no registration-date integration with Profile.API — never a fabricated 0.
   * Render "Not yet measured".
   */
  averageDaysToFirstSale?: number | null;
  targetRate: number;
  /** Computed server-side as firstSaleRate >= targetRate. */
  onTarget: boolean;
  /** Confirmed first-sale MARKS, all time. Marks >= users: a profile can hold more than one. */
  totalConfirmedMarksLifetime: number;
  /** Confirmed MARKS in the requested window — deliberately not usersAchievedFirstSale. */
  confirmedMarksInWindow: number;
  /** Denominator of anomalyRatePercent. 0% of 0 marks is not 0% of 400 — always read them together. */
  marksInTrailing30Days: number;
  flaggedInTrailing30Days: number;
  anomalyRatePercent: number;
  anomalyRateThresholdPercent: number;
  anomalyRateExceedsThreshold: boolean;
  confirmedMarksThreshold: number;
  confirmedMarksMeetsThreshold: boolean;
}

/**
 * Top-level admin metrics summary.
 * Maps to AdminMetricsResponse (AdminMetricsResponse.cs).
 * Engagement KPIs are NESTED under the `engagement` field.
 */
/**
 * Admin Streaks block from StreakMetrics (AdminMetricsResponse.cs). One definition shared with
 * the mobile AdminPage, ratified by DEC-2026-10-06 (admin Streaks KPI meaning).
 */
export interface StreakMetrics {
  /** Denominator: accounts with any card move in the trailing 7 days (interim). */
  totalActiveUsers: number;
  /** Accounts in the denominator on a current run of 3+ completion days (ending today or yesterday, UTC). */
  usersWithThreePlusStreak: number;
  /** usersWithThreePlusStreak / totalActiveUsers, percent, 0-100. */
  threePlusStreakRate: number;
  /** Mean current streak across the denominator, one decimal. */
  averageStreak: number;
  /** Longest run held in recorded history; not a current figure. */
  longestStreakInSystem: number;
  /** null: no target is set. */
  targetRate: number | null;
  onTarget: boolean;
  /** e.g. "of accounts with a card move in 7 days" */
  denominatorLabel: string;
  historyStartUtc: string | null;
  historyDays: number;
  historyAccounts: number;
  includesTestAccounts: boolean;
  /** Server-built, e.g. "History since 2026-07-05 (102 days, 292 accounts)". */
  coverageLine: string;
}

export interface AdminMetricsSummary {
  generatedAt: string;
  totalUsers: number;
  activeUsersLast30Days: number;
  /** Real-time engagement KPIs — nested per 17-GR-01. */
  engagement: EngagementMetrics;
  /**
   * First-sale KPIs — nested, and optional here rather than required: the server has always
   * sent it, but a type that says `required` would make an older deployment a render crash
   * instead of a hidden panel. Every consumer must guard.
   */
  firstSale?: FirstSaleMetrics;
  /** Optional for the same reason as `firstSale`: an older deployment does not send it. */
  streaks?: StreakMetrics;
  window: {
    days: number;
    label: string;
  };
}

/**
 * Weekly-cohort retention series returned by GET /api/admin/metrics/retention
 * (DEC-2026-09-08 Ruling A). Maps to RetentionSeriesResponse (AdminMetricsResponse.cs).
 * Data is parallel arrays: dates[i] corresponds to weekOneRetention[i] etc.
 * null entries mean no data (empty cohort or no run that day) and must never be shown as 0.
 */
export interface RetentionMetrics {
  /** Rolling window in days that was requested (7, 30, or 90). */
  window: number;
  /** ISO-8601 date strings ("yyyy-MM-dd"), ascending. */
  dates: string[];
  /** Share (%) of the signup-week cohort with a qualifying action on or after week 1. null = no data. */
  weekOneRetention: (number | null)[];
  /** Same for week 2. null = no data. */
  weekTwoRetention: (number | null)[];
  /** Same for week 4. null = no data. */
  weekFourRetention: (number | null)[];
}

/**
 * Tier distribution percentages — each value is 0-100.
 * Maps to TierDistribution (NpsModels.cs).
 * This is an OBJECT with named tiers — not an array.
 */
/**
 * Percentage breakdown from GET /api/admin/metrics/revenue.
 *
 * Mirrors the C# TierDistribution built by TierDistributionCalculator, which emits
 * SEVEN values. `pro` and `unknown` were missing here, so the wire carried them and
 * the client discarded them:
 *
 *   - `pro` is a live tier — DEC-2026-07-30 collapsed the ladder to Free/Basic/Pro/
 *     Premium and added Pro precisely because it was a dead key before. Dropping it
 *     from the type re-creates the "tier vanishes from the report" bug US-17-GR-38
 *     exists to prevent, one layer up.
 *   - `unknown` is the never-subscribed bucket (see src/utils/tierDistribution.ts).
 *     It is not a tier and must never be drawn as one, but a panel that silently
 *     renders nothing when it is 100% tells the reader less than one that says so.
 *
 * Retired tiers (entrepreneur/growth/elite) stay listed on purpose: they are real
 * subscriptions with no listed price, and folding them away is the same bug again.
 */
export interface TierDistribution {
  basic: number;
  pro: number;
  entrepreneur: number;
  growth: number;
  premium: number;
  elite: number;
  /** Percentage of profiles with no subscription record at all. Not a tier. */
  unknown: number;
}

/**
 * Work item 17471: the customer-acquisition-cost anchor.
 * Maps to CustomerAcquisitionCostAnchor (NpsModels.cs).
 *
 * The tile used to render a bare configured number of 30 as a measurement. DEC-2026-07-23
 * ratifies CAC at $40-60 per activated signup, paid-ads-only, and requires an as-of date, a
 * named source, and an unvalidated-estimate stamp. The anchor carries all of that so the
 * surface cannot show a figure without also showing what it is and where it came from.
 */
export interface CustomerAcquisitionCostAnchor {
  /** The figure in USD, or null when no compliant figure exists. */
  valueUsd: number | null;
  /** Why no figure is shown. Populated exactly when `valueUsd` is null. */
  unavailableReason: string | null;
  /** The date the figure was struck (DEC-2026-07-23 condition C2). */
  asOfUtc: string | null;
  /** The named source it came from (DEC-2026-07-23 condition C2). */
  source: string | null;
  /** The unit of measure, so this can never be blended with the signup-CPA gates (C1). */
  unit: string;
  /** Low bound of the ratified band. */
  ratifiedBandLowUsd: number;
  /** High bound of the ratified band. */
  ratifiedBandHighUsd: number;
  /** Always true — DEC-2026-07-23 condition C4 stamps this anchor an unvalidated estimate. */
  isUnvalidatedEstimate: boolean;
}

/** What the revenue panel should draw for CAC. */
export type CacDisplay =
  | { kind: 'value'; anchor: CustomerAcquisitionCostAnchor; valueUsd: number }
  | { kind: 'unavailable'; reason: string; anchor: CustomerAcquisitionCostAnchor | null };

/** Fallback copy when the server sent no reason of its own. */
const CAC_UNAVAILABLE_FALLBACK =
  'No customer acquisition cost is available that satisfies DEC-2026-07-23 — the figure must be ' +
  'inside the ratified $40-60 per activated signup band and carry an as-of date and a named source.';

/**
 * Work item 17471, acceptance criterion 6. Decides whether a CAC figure may render as a plain
 * measured number. It may only when the payload is the anchor shape, carries a value, carries
 * an as-of date and a named source, and sits inside the ratified band.
 *
 * The band and provenance are re-checked here rather than trusted from the server on purpose.
 * A deployed API predating this change sends a bare number (the shipped default was 30, which
 * breaches the band on every count), and this is the surface where "render" actually happens —
 * so this is the check that has to hold for the decision to be honoured on screen.
 */
export function resolveCacDisplay(
  raw: CustomerAcquisitionCostAnchor | number | null | undefined,
): CacDisplay {
  if (raw === null || raw === undefined) {
    return { kind: 'unavailable', reason: CAC_UNAVAILABLE_FALLBACK, anchor: null };
  }

  if (typeof raw === 'number') {
    // Legacy wire shape: a bare configured number with no date and no source.
    return {
      kind: 'unavailable',
      reason:
        'This deployment reports a customer acquisition cost with no as-of date and no named ' +
        'source, which DEC-2026-07-23 does not permit to be shown. It is withheld rather than ' +
        'presented as a measurement.',
      anchor: null,
    };
  }

  const reason = raw.unavailableReason?.trim() || CAC_UNAVAILABLE_FALLBACK;

  if (raw.valueUsd === null || raw.valueUsd === undefined || !Number.isFinite(raw.valueUsd)) {
    return { kind: 'unavailable', reason, anchor: raw };
  }
  if (!raw.asOfUtc || !raw.source?.trim()) {
    return { kind: 'unavailable', reason, anchor: raw };
  }
  if (raw.valueUsd < raw.ratifiedBandLowUsd || raw.valueUsd > raw.ratifiedBandHighUsd) {
    return { kind: 'unavailable', reason, anchor: raw };
  }

  return { kind: 'value', anchor: raw, valueUsd: raw.valueUsd };
}

/**
 * Revenue metrics from GET /api/admin/metrics/revenue.
 * Maps to RevenueMetricsResponse (NpsModels.cs).
 */
export interface RevenueMetrics {
  /** Percentage breakdown of subscribers per tier — object with named properties. */
  tierDistribution: TierDistribution;
  /** Percentage of users who converted from free to a paid tier. */
  freeToPaidConversion: number;
  /** Monthly churn rate as a percentage of paid subscribers. */
  monthlyChurn: number;
  /** Average lifetime value (USD) across all paid profiles. */
  averageLtv: number;
  /**
   * Work item 17471: the customer-acquisition-cost anchor, carried with the provenance
   * DEC-2026-07-23 requires before any CAC figure may be shown.
   *
   * Typed to admit a bare `number` as well, because that is what a deployed API predating
   * this change still sends. `resolveCacDisplay` treats that legacy shape as unavailable —
   * a bare number carries no as-of date and no named source, so by the decision it is not
   * showable, and the SPA closes the breach even before the API half deploys.
   */
  customerAcquisitionCost: CustomerAcquisitionCostAnchor | number | null;
  // PRD targets
  ltvTarget: number;
  churnTarget: number;
  conversionTarget: number;
  /**
   * True when the revenue computation threw on the server — every other field on
   * this payload is meaningless, not measured (US-17-GR-34). The HTTP status
   * stays 200 on this path; this flag is the only signal. Never render the
   * numeric fields as zero when this is true.
   */
  dataUnavailable: boolean;
  /**
   * UTC timestamp of the newest metrics snapshot the values were read from.
   * null when unknown. The nightly aggregator writes snapshots on a ~24h
   * cadence — treat a value older than 36h as stale (US-17-GR-34).
   */
  asOfUtc: string | null;
}

/**
 * NPS metrics from GET /api/admin/metrics/nps.
 * Maps to NpsMetricsResponse (NpsModels.cs).
 */
export interface NpsMetrics {
  /** NPS score: promoters% - detractors%. Range -100 to +100. */
  score: number;
  /** Percentage of respondents who are Promoters (score 9-10). */
  promoters: number;
  /** Percentage of respondents who are Passives (score 7-8). */
  passives: number;
  /** Percentage of respondents who are Detractors (score 0-6). */
  detractors: number;
  /** Total number of NPS responses. */
  totalResponses: number;
  /** PRD target NPS score. */
  scoreTarget: number;
  /**
   * True when the NPS computation threw on the server — every other field on
   * this payload is meaningless, not measured (US-17-GR-34). The HTTP status
   * stays 200 on this path; this flag is the only signal. Never render the
   * numeric fields as zero when this is true.
   */
  dataUnavailable: boolean;
  /**
   * Why dataUnavailable is true (story 17788). "noResponses" = nobody answered in the
   * trailing 30 days (no data, not a zero score); "failed" = server error. Absent/null on
   * an older API, which must be treated as "failed".
   */
  dataUnavailableReason?: 'noResponses' | 'failed' | null;
  /**
   * UTC timestamp of the newest metrics snapshot the values were read from.
   * null when unknown. The nightly aggregator writes snapshots on a ~24h
   * cadence — treat a value older than 36h as stale (US-17-GR-34).
   */
  asOfUtc: string | null;
}

/** Name/count pair for top service category entries. */
export interface ServiceCategoryCount {
  name: string;
  count: number;
}

/**
 * Outsourcing marketplace health metrics from GET /api/admin/metrics/outsourcing.
 * Maps to OutsourcingMetricsResponse (AdminMetricsResponse.cs).
 */
export interface OutsourcingMetrics {
  /** Count of active outsource requests (Open/BiddingClosed/Awarded/InProgress) in window. */
  totalActiveRequests: number;
  /** Total bids submitted in window. */
  totalBidsSubmitted: number;
  /** Average bids per active outsource request. */
  averageBidsPerRequest: number;
  /** Median elapsed hours from request creation to first bid. */
  medianTimeToFirstBidHours: number;
  /** Ratio of requests reaching Awarded/InProgress/Completed to all requests (0..1). */
  bidAcceptanceRate: number;
  /** Average elapsed days from creation to completion for completed requests. */
  averageOutsourcedTaskCompletionDays: number;
  /**
   * 5-element array of rating-star counts.
   * Index 0 = 1-star count, index 4 = 5-star count.
   */
  providerRatingDistribution: number[];
  /** Top service categories by request count. */
  topServiceCategories: ServiceCategoryCount[];
  /** Ratio of unique requestors in window to WAU count (0..1). */
  outsourcingAdoptionRate: number;
  /** PRD adoption target (0..1): 20% of WAU should outsource at least one task. */
  adoptionTarget: number;
  /**
   * Work item 17426: true when the figures above could not be read and are therefore unknown,
   * not zero. The API used to catch every failure and answer 200 with zeros and no flag, so a
   * broken ServiceManager S2S credential and an idle marketplace were the same page - and zero
   * adoption is a number an operator acts on. Optional so an older API build still parses.
   */
  dataUnavailable?: boolean;
  /** Work item 17426: short operator-facing reason, present when dataUnavailable is true. */
  dataUnavailableReason?: string;
}

/**
 * Per-ISO-week check-in metrics row from GET /api/admin/metrics/check-ins.
 * weekStart is the ISO-week Monday, "yyyy-MM-dd".
 * Kept/missed are a cohort of the week the commitment was MADE in — Open/Replanned
 * commitments count only in commitmentsMade (story 19005).
 */
export interface CheckInWeekMetrics {
  weekKey: string;
  weekStart: string;
  checkInsStarted: number;
  checkInsCompleted: number;
  commitmentsMade: number;
  commitmentsKept: number;
  commitmentsMissed: number;
}

/**
 * GET /api/admin/metrics/check-ins response. Dense series (zero weeks
 * included), ordered NEWEST FIRST.
 */
export interface CheckInWeeklyMetrics {
  weeks: CheckInWeekMetrics[];
}

/** Tier labels in display order (DEC-2026-07-30: Free/Basic/Pro/Premium). */
export const DECISION_TIERS = ['free', 'basic', 'pro', 'premium'] as const;
export type DecisionTier = (typeof DECISION_TIERS)[number];

/** One ISO week of GET /api/TaskManager/Admin/Agent/DecisionsPerWeek. Weeks with no events are absent. */
export interface DecisionsPerWeekWeek {
  isoWeek: string;
  weekStartUtc: string;
  totalDecisions: number;
  /**
   * null when the API predates the tier split (old shape has no byTier): the split is
   * unavailable, never zeros.
   */
  byTier: Record<DecisionTier, number> | null;
  /** Decisions whose profile tier lookup failed. 0 on an older API. */
  tierUnresolved: number;
}

export interface DecisionsPerWeekResult {
  from: string;
  to: string;
  /** The tier filter that was applied, or null when unfiltered. */
  tier: string | null;
  weeks: DecisionsPerWeekWeek[];
  averageDecisionsPerWeek: number;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function windowParam(w: MetricWindow): string {
  return `?window=${w}`;
}

// Cold-start budget (90 s/attempt, 180 s total): the first call can wake a cold TaskManager, so
// each attempt must outlast the gateway's 75000 ms ceiling (work item 20640); the 10 s default
// would abort before the gateway ever answers (work item 20671).
async function get<T>(path: string): Promise<T> {
  const response = await authenticatedFetch(path, undefined, { retryConfig: COLDSTART_RETRY_CONFIG });
  if (!response.ok) {
    throw new Error(`Admin metrics request failed: HTTP ${response.status} ${path}`);
  }
  return response.json() as Promise<T>;
}

function parseDecisionsWeek(raw: unknown): DecisionsPerWeekWeek {
  const w = (raw ?? {}) as Record<string, unknown>;
  const bt = w.byTier as Record<string, unknown> | null | undefined;
  const byTier =
    bt && typeof bt === 'object'
      ? (Object.fromEntries(DECISION_TIERS.map((t) => [t, Number(bt[t] ?? 0)])) as Record<DecisionTier, number>)
      : null;
  return {
    isoWeek: String(w.isoWeek ?? ''),
    weekStartUtc: String(w.weekStartUtc ?? ''),
    totalDecisions: Number(w.totalDecisions ?? 0),
    byTier,
    tierUnresolved: Number(w.tierUnresolved ?? 0),
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * GET /api/admin/metrics — top-level KPI summary.
 * Optionally filtered by time window (days).
 * Returns AdminMetricsSummary where engagement KPIs live in .engagement.*.
 */
export async function getAdminMetrics(window: MetricWindow = 30): Promise<AdminMetricsSummary> {
  return get<AdminMetricsSummary>(`/api/admin/metrics${windowParam(window)}`);
}

/**
 * GET /api/admin/metrics/retention — day-1/7/30 retention parallel arrays.
 * Use averageFromSeries() (below) to compute averages for display.
 */
export async function getRetentionSeries(window: MetricWindow = 30): Promise<RetentionMetrics> {
  return get<RetentionMetrics>(`/api/admin/metrics/retention${windowParam(window)}`);
}

/**
 * GET /api/admin/metrics/revenue — tier distribution (object), LTV, CAC, churn,
 * freeToPaidConversion. Note: tierDistribution is an object with named properties,
 * NOT an array.
 */
export async function getRevenueMetrics(): Promise<RevenueMetrics> {
  return get<RevenueMetrics>('/api/admin/metrics/revenue');
}

/**
 * GET /api/admin/metrics/nps — NPS score breakdown.
 * Fields: score, totalResponses, promoters, passives, detractors, scoreTarget.
 */
export async function getNpsMetrics(): Promise<NpsMetrics> {
  return get<NpsMetrics>('/api/admin/metrics/nps');
}

/**
 * GET /api/admin/metrics/outsourcing — marketplace health metrics.
 * Real fields: totalActiveRequests, totalBidsSubmitted, averageBidsPerRequest,
 * bidAcceptanceRate, medianTimeToFirstBidHours, providerRatingDistribution,
 * topServiceCategories, outsourcingAdoptionRate.
 */
export async function getOutsourcingMetrics(window: MetricWindow = 30): Promise<OutsourcingMetrics> {
  return get<OutsourcingMetrics>(`/api/admin/metrics/outsourcing${windowParam(window)}`);
}

/**
 * GET /api/admin/metrics/check-ins — per-ISO-week check-in and commitment
 * counts (story 19005). `weeks` is the number of ISO weeks to return
 * (server default 8, valid 1..52).
 */
export async function getCheckInWeeklyMetrics(weeks = 8): Promise<CheckInWeeklyMetrics> {
  return get<CheckInWeeklyMetrics>(`/api/admin/metrics/check-ins?weeks=${weeks}`);
}

/**
 * GET /api/TaskManager/Admin/Agent/DecisionsPerWeek — agent decisions per ISO week, split by tier
 * (spec 018 FR-12). A failed request throws, so callers can tell it from a real zero-decision
 * window (a 200 with no weeks).
 */
export async function getDecisionsPerWeek(
  from: Date | string,
  to: Date | string,
): Promise<DecisionsPerWeekResult> {
  const iso = (d: Date | string) => (d instanceof Date ? d.toISOString() : d);
  const query = `from=${encodeURIComponent(iso(from))}&to=${encodeURIComponent(iso(to))}`;
  const raw = await get<Partial<DecisionsPerWeekResult> & { weeks?: unknown[] }>(
    `/api/TaskManager/Admin/Agent/DecisionsPerWeek?${query}`,
  );
  const weeks = (Array.isArray(raw.weeks) ? raw.weeks : []).map((w) => parseDecisionsWeek(w));
  return {
    from: raw.from ?? iso(from),
    to: raw.to ?? iso(to),
    tier: raw.tier ?? null,
    weeks,
    averageDecisionsPerWeek: Number(raw.averageDecisionsPerWeek ?? 0),
  };
}

// ---------------------------------------------------------------------------
// Client-side helpers
// ---------------------------------------------------------------------------

/**
 * Compute average of a nullable parallel retention array, ignoring null entries.
 * Returns null when all entries are null (no data yet).
 */
export function averageFromSeries(series: (number | null)[]): number | null {
  const values = series.filter((v): v is number => v !== null);
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

export const adminMetricsService = {
  getAdminMetrics,
  getRetentionSeries,
  getRevenueMetrics,
  getNpsMetrics,
  getOutsourcingMetrics,
  getCheckInWeeklyMetrics,
  getDecisionsPerWeek,
  averageFromSeries,
};
